import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AllDeviceInfo, Language } from '@interfaces';
import { SnackbarService } from '@shared/snack-bar/snack-bar.service';
import { RouterFacade } from '@state/router';

import { defaultLanguages } from '@constants';
import { TranslateService } from '@ngx-translate/core';
import { DeviceInfoService } from '@core/services/device-info.service';
import { CommonsFacade } from '@state/commons';
import { UserAuthFacade } from '@state/user-auth';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('safeLogoutInfoRef') safeLogoutInfoRef!: ElementRef;
  @ViewChild('mainTitleText') mainTitleText!: ElementRef;

  public loginForm = this.fb.group({
    email: ['', Validators.compose([Validators.required, Validators.email])],
    password: ['', Validators.compose([Validators.required, Validators.minLength(5)])],
  });

  public errorMessage$ = this.userAuthFacade.errorMessage$;
  public loading$ = this.userAuthFacade.loading$;

  public currentUserLang: Language;
  public safeLogoutInfo = false;

  public APIAvailable = true;

  public isPasswordVisible = false;

  private redirect: string | null = null;

  public deviceInfo: AllDeviceInfo;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userAuthFacade: UserAuthFacade,
    private readonly activatedRoute: ActivatedRoute,
    private readonly snackService: SnackbarService,
    private readonly routerFacade: RouterFacade,
    private readonly ls: LocalStorageService,
    private readonly translateService: TranslateService,
    private readonly commonsFacade: CommonsFacade,
    private readonly deviceInfoService: DeviceInfoService
  ) {}

  public ngOnInit(): void {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });

    const { email, message, redirect, safeLogout } = this.activatedRoute.snapshot.queryParams;

    this.commonsFacade.lastAPIPingSuccessfull$.pipe(takeUntil(this.destroy$)).subscribe(APIPingSuccess => {
      if (!APIPingSuccess) {
        // if API connection failed - setup langs with default Languages hardcoded
        this.setupUserLang([...defaultLanguages]);
        this.APIAvailable = false;
        return;
      }

      this.APIAvailable = true;
    });

    this.commonsFacade
      .getLanguages$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(availableLanguages => {
        // setup user lang with languages fetched from API
        this.setupUserLang([...availableLanguages]);
      });

    if (email) {
      this.loginForm.patchValue({ email });
    }

    if (message) {
      this.snackService.showInfo(message);
    }

    if (redirect) {
      this.redirect = redirect;
    }

    if (safeLogout) {
      this.safeLogoutInfo = true;
      this.safeLogoutInfoRef?.nativeElement.focus();
    }

    this.userAuthFacade.loginSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(({ redirect }) => {
      if (redirect) {
        this.routerFacade.changeRoute({ linkParams: ['/init-user'], extras: { queryParams: { redirect } } });
      } else {
        this.routerFacade.changeRoute({ linkParams: ['/init-user'] });
      }
    });
  }

  public ngAfterViewInit() {
    this.mainTitleText?.nativeElement.focus();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public userLangChanged($event: Language): void {
    this.currentUserLang = $event;
  }

  public submitLoginForm(): boolean {
    if (!this.loginForm.valid) {
      return false;
    }

    this.loginForm.markAsPristine();
    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;
    const redirect = this.redirect;
    this.snackService.closeAll();
    this.userAuthFacade.login(email, password, this.currentUserLang.id, redirect);
    return false;
  }

  private setupUserLang(langs: Language[]): void {
    const { langKey } = this.activatedRoute.snapshot.queryParams;

    // lang from query params is most important
    const langInQueryParams = langs.find(({ key }) => key === langKey);

    // if no lang in query params then localStorage
    const langInLS = this.ls.getUserLang();

    // if no lang in Query & no in LS use default
    const defaultLang = langs.find(({ key }) => key === 'en');

    // 1. langInQuery 2. langInLS 3. defaultLang
    const langToUse = langInQueryParams?.key ? langInQueryParams : langInLS?.key ? langInLS : defaultLang;

    this.ls.setUserLang(langToUse);
    this.translateService.use(langToUse.key);
    this.currentUserLang = langToUse;
  }

  public scrollToInput(event: FocusEvent) {
    if (this.deviceInfo.deviceTypeDetected === 'PHONE') {
      const target = event.target as HTMLElement;
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }
}
